// ebInterface 6.1 XML-Generator (österreichische eRechnung)
// Spezifikation: WKO ebInterface 6.1 Schema

import { type Rechnung, type Aussteller } from '../types';
import { type EuroCents } from '../../../types/branded';

// ── Hilfsfunktionen ────────────────────────────────────────────────────────

function xmlEscape(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

function centsToDecimal(cents: EuroCents): string {
  return (cents / 100).toFixed(2);
}

// ── ebInterface 6.1 XML ───────────────────────────────────────────────────

export function generateEbInterface61(rechnung: Rechnung, aussteller: Aussteller): string {
  const e = rechnung.empfaenger;
  const ausName = [aussteller.titelVor, aussteller.name, aussteller.titelNach]
    .filter(Boolean).join(' ');

  // USt-Aufschlüsselung
  const ustGruppen = new Map<number, { netto: number; ust: number }>();
  for (const p of rechnung.positionen) {
    const g = ustGruppen.get(p.ustSatz) ?? { netto: 0, ust: 0 };
    ustGruppen.set(p.ustSatz, {
      netto: g.netto + p.gesamtNetto,
      ust:   g.ust   + p.gesamtUst,
    });
  }

  const posLines = rechnung.positionen.map((p, idx) => `
    <ItemList>
      <ListLineItem>
        <Position>${idx + 1}</Position>
        <Description>${xmlEscape(p.bezeichnung)}</Description>
        <Quantity Unit="${xmlEscape(p.einheit)}">${p.menge}</Quantity>
        <UnitPrice>${centsToDecimal(p.einzelpreisNetto)}</UnitPrice>
        <VATRate>${p.ustSatz}.00</VATRate>
        <LineItemAmount>${centsToDecimal(p.gesamtNetto)}</LineItemAmount>
      </ListLineItem>
    </ItemList>`).join('');

  const taxLines = [...ustGruppen.entries()].map(([satz, g]) => `
    <VAT>
      <VATRate>${satz}.00</VATRate>
      <TaxedAmount>${centsToDecimal(g.netto as EuroCents)}</TaxedAmount>
      <VATAmount>${centsToDecimal(g.ust as EuroCents)}</VATAmount>
    </VAT>`).join('');

  const xml = `<?xml version="1.0" encoding="UTF-8"?>
<Invoice xmlns="http://www.ebinterface.at/schema/6p1/"
         xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
         xsi:schemaLocation="http://www.ebinterface.at/schema/6p1/ https://www.ebinterface.at/schema/6p1/ebInterface-6p1.xsd"
         GeneratingSystem="AMed v6.7"
         DocumentTitle="Rechnung"
         Language="ger"
         IsDuplicate="false">

  <InvoiceNumber>${xmlEscape(rechnung.rechnungsNr)}</InvoiceNumber>
  <InvoiceDate>${rechnung.datum}</InvoiceDate>

  <Biller>
    <VATIdentificationNumber>${xmlEscape(aussteller.uid)}</VATIdentificationNumber>
    <Address>
      <Name>${xmlEscape(ausName)}</Name>
      <Street>${xmlEscape(aussteller.strasse)}</Street>
      <ZIP>${xmlEscape(aussteller.plz)}</ZIP>
      <Town>${xmlEscape(aussteller.ort)}</Town>
      <Country CountryCode="${aussteller.land}" />
    </Address>
    <Email>${xmlEscape(aussteller.email ?? '')}</Email>
  </Biller>

  <InvoiceRecipient>
    ${e.uid !== undefined && e.uid !== '' ? `<VATIdentificationNumber>${xmlEscape(e.uid)}</VATIdentificationNumber>` : ''}
    <Address>
      <Name>${xmlEscape(e.firma)}</Name>
      <Street>${xmlEscape(e.strasse)}</Street>
      <ZIP>${xmlEscape(e.plz)}</ZIP>
      <Town>${xmlEscape(e.ort)}</Town>
      <Country CountryCode="${xmlEscape(e.land)}" />
    </Address>
  </InvoiceRecipient>

  <Details>
    ${posLines}
  </Details>

  <Tax>
    ${taxLines}
  </Tax>

  <TotalGrossAmount>${centsToDecimal(rechnung.summeBrutto)}</TotalGrossAmount>
  <PayableAmount>${centsToDecimal(rechnung.summeBrutto)}</PayableAmount>

  <PaymentMethod>
    <PaymentConditions>
      <DueDate>${rechnung.faelligAm}</DueDate>
    </PaymentConditions>
    <UniversalBankTransaction>
      <BeneficiaryAccount>
        <IBAN>${xmlEscape(aussteller.iban)}</IBAN>
        <BIC>${xmlEscape(aussteller.bic)}</BIC>
        <BankAccountOwner>${xmlEscape(ausName)}</BankAccountOwner>
      </BeneficiaryAccount>
      <RemittanceIdentifier>${xmlEscape(rechnung.rechnungsNr)}</RemittanceIdentifier>
    </UniversalBankTransaction>
  </PaymentMethod>

</Invoice>`;

  return xml;
}

// ── Download-Hilfsfunktion ────────────────────────────────────────────────

export function downloadEbInterface(rechnung: Rechnung, aussteller: Aussteller): void {
  const xml = generateEbInterface61(rechnung, aussteller);
  const blob = new Blob([xml], { type: 'application/xml;charset=utf-8' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = `eRechnung_${rechnung.rechnungsNr}.xml`;
  a.click();
  URL.revokeObjectURL(url);
}
